import { CreateKitAssetItem } from '../../../../redux/api/fymAPI.types';
import { itemsCredits } from '../studio/studioRules';

export interface KitItemsSplit {
  /** The items to make now: every item when the balance pays for all, else only the free images. */
  make: CreateKitAssetItem[];
  /** The videos left out because the balance cannot pay for all of them; empty when nothing is left out. */
  skipped: CreateKitAssetItem[];
  /** The credits that the left-out videos need together. */
  skippedCredits: number;
}

/**
 * The owner rule of 2026-10-06 for "Make the kit" and "Make them again": the free images are
 * always made at once. When the balance (plan plus packs) cannot pay for every video, no video is
 * made: never a part of the videos. The caller offers the credit packs for the skipped videos.
 * `isShort(cost)` is `useVideoCreditGate().isShort`. Pure.
 */
export function splitKitItemsForBalance(
  items: ReadonlyArray<CreateKitAssetItem>,
  isShort: (cost: number) => boolean
): KitItemsSplit {
  const free = items.filter((item) => itemsCredits([item]) === 0);
  const paid = items.filter((item) => itemsCredits([item]) > 0);
  const paidCredits = itemsCredits(paid);
  if (paid.length === 0 || !isShort(paidCredits)) {
    return { make: [...items], skipped: [], skippedCredits: 0 };
  }
  return { make: free, skipped: paid, skippedCredits: paidCredits };
}

/** The same rule for "Make them again", which names current assets with their credits. Pure. */
export function splitRemakeForBalance<T extends { assetUuid: string; credits: number }>(
  items: ReadonlyArray<T>,
  isShort: (cost: number) => boolean
): { make: T[]; skipped: T[]; skippedCredits: number } {
  const paid = items.filter((item) => item.credits > 0);
  const paidCredits = paid.reduce((sum, item) => sum + item.credits, 0);
  if (paid.length === 0 || !isShort(paidCredits)) {
    return { make: [...items], skipped: [], skippedCredits: 0 };
  }
  return { make: items.filter((item) => item.credits === 0), skipped: paid, skippedCredits: paidCredits };
}
